import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  LinearProgress,
  linearProgressClasses,
  Button,
  Stack,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  IconButton
 } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { theme } from '../../../theme/theme';
import { styled, keyframes } from '@mui/material/styles';
import {
  InternetPackageWithRelations,
  useGetInternetUsageByPropertyIdQuery,
  usePatchPropertyMutation,
  usePostInternetBoosterMutation,
  usePostInternetUpgradeMutation,
  useGetInternetProRataQuery,
  useGetInternetPackagesQuery
} from '../../../redux/residentPortalApi';

import { formatCurrency } from "../../../utils/currencyUtil";
import BoltIcon from '@mui/icons-material/BoltOutlined';
import UpgradeIcon from '@mui/icons-material/UpgradeOutlined';
import CancelIcon from '@mui/icons-material/CancelOutlined';

const pulse = keyframes`
    0% { opacity: 0.6; transform: scale(0.8); }
    50% { opacity: 1; transform: scale(1.2); }
    100% { opacity: 0.6; transform: scale(0.8); }
`;

const useStyles = makeStyles({
  sectionTitle: {
    margin: theme.spacing(2, 0),
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    border: '1px solid rgba(0,0,0,0.05)',
    padding: theme.spacing(2.5),
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    '&:hover': {
      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
      transform: 'translateY(-2px)',
    },
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      right: 0,
      width: '120px',
      height: '120px',
      background: 'radial-gradient(circle at top right, rgba(25,118,210,0.08), transparent 60%)',
      zIndex: 1,
    },
  },
  badge: {
    position: 'absolute',
    top: '10px',
    right: '10px',
    padding: theme.spacing(0.5, 1.25),
    borderRadius: 8,
    fontSize: '0.72rem',
    letterSpacing: 0.5,
    fontWeight: 700,
    zIndex: 2,
    boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
    color: '#fff',
    backdropFilter: 'blur(2px)'
  },
  cardContent: {
    paddingBottom: theme.spacing(1),
  },
  usageText: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(0.75),
    color: 'rgba(0,0,0,0.65)'
  },
});

const BorderLinearProgress = styled(LinearProgress, {
  shouldForwardProp: (prop) => prop !== 'usageColor',
})<{ usageColor: string }>(({ theme, usageColor }) => ({
  height: 10,
  borderRadius: 5,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: theme.palette.grey[200],
    ...theme.applyStyles?.('dark', {
      backgroundColor: theme.palette.grey[800],
    }),
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
    backgroundColor: usageColor,
  },
}));

const UsageStatus = ({ isUnlimited, usagePercentage }: { isUnlimited: boolean; usagePercentage: number }) => {
  if (isUnlimited) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
          <Box component="span" sx={{ color: '#4caf50', fontWeight: 'bold', display: 'inline-flex', alignItems: 'center' }}>
            ∞ Unlimited
          </Box>
          data - no usage limits
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: '#4caf50',
            animation: `${pulse} 2s infinite`
          }} />
          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>Active</Typography>
        </Box>
      </Box>
    );
  }

  const usageLabel =
    usagePercentage > 100 ? 'Over cap' :
    usagePercentage > 90 ? 'High usage' :
      usagePercentage > 70 ? 'Moderate usage' :
        'Good usage';

  return (
    <Box sx={{ mt: 0.5 }}>
      <Typography variant="caption" color="text.secondary">{usageLabel}</Typography>
    </Box>
  );
};

const ManageInternetPackage: React.FC<{ propertyId: string; internetPackage: InternetPackageWithRelations }> = ({
                                                                                                                  propertyId,
                                                                                                                  internetPackage
                                                                                                                }) => {
  const classes = useStyles();
  const { data: internetConsumption, isError, refetch: refetchUsage } = useGetInternetUsageByPropertyIdQuery({ propertyId });
  const totalDataUsed = internetConsumption?.totalDataUsedGb ?? 0;
  const isUnlimited = !internetPackage.dataCap || internetPackage.dataCap === 0;
  const usagePercentage = isUnlimited ? 0 : (Math.round(totalDataUsed) / internetPackage.dataCap) * 100;

  const usageColor = isUnlimited
    ? '#4caf50'
    : usagePercentage > 100
      ? '#b71c1c'
      : usagePercentage > 90
        ? '#d32f2f'
        : usagePercentage > 70
          ? '#f57c00'
          : usagePercentage > 50
            ? '#1976d2'
            : '#1a90ff';

  const [patchProperty, {isLoading: isCancelling}] = usePatchPropertyMutation();
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState<string>('');
  const [snackSeverity, setSnackSeverity] = useState<'success' | 'error' | 'info'>('success');

  const [purchaseBooster, { isLoading: isPurchasingBooster }] = usePostInternetBoosterMutation();
  const [upgradeSubscription, { isLoading: isUpgrading }] = usePostInternetUpgradeMutation();
  const { data: allPackages } = useGetInternetPackagesQuery();

  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [selectedUpgradeId, setSelectedUpgradeId] = useState<string>('');

  const { data: proration } = useGetInternetProRataQuery(
    { propertyId, packageId: selectedUpgradeId || undefined },
    { skip: !upgradeOpen || !selectedUpgradeId }
  );

  const showSnack = (message: string, severity: 'success' | 'error' | 'info' = 'success') => {
    setSnackMsg(message);
    setSnackSeverity(severity);
    setSnackOpen(true);
  };

  const handleCancel = async () => {
    try {
      await patchProperty({ propertyPartial: { id: propertyId, internetPackageId: undefined as any } }).unwrap();
      showSnack('Subscription canceled successfully', 'success');
    } catch (e) {
      console.error('Failed to cancel subscription', e);
      showSnack('Failed to cancel subscription. Please try again.', 'error');
    }
  };

  const handleBuyBooster = async () => {
    try {
      await purchaseBooster({ body: { propertyId } }).unwrap();
      showSnack('Booster purchased. Data cap has been reset.', 'success');
      refetchUsage();
    } catch (e) {
      console.error('Failed to purchase booster', e);
      showSnack('Failed to purchase booster. Please try again.', 'error');
    }
  };

  const openUpgrade = () => {
    setSelectedUpgradeId('');
    setUpgradeOpen(true);
  };

  const closeUpgrade = () => {
    setUpgradeOpen(false);
  };

  const confirmUpgrade = async () => {
    if (!selectedUpgradeId) return;
    try {
      await upgradeSubscription({ body: { propertyId, newPackageId: selectedUpgradeId } }).unwrap();
      showSnack('Package upgraded successfully.', 'success');
      setUpgradeOpen(false);
      refetchUsage();
    } catch (e) {
      console.error('Failed to upgrade package', e);
      showSnack('Failed to upgrade package. Please try again.', 'error');
    }
  };

  return (
    <Box>
      <Typography variant="h3" className={classes.sectionTitle}>
        Your Current Package
      </Typography>
        <Card className={classes.card}>
          <Box
            className={classes.badge}
            sx={{
              background: isUnlimited
                ? 'linear-gradient(135deg, #43a047, #2e7d32)'
                : 'linear-gradient(135deg, #ff9800, #f57c00)'
            }}
          >
            {isUnlimited ? 'UNLIMITED' : 'CAPPED'}
          </Box>

          <CardContent className={classes.cardContent}>
            <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>{internetPackage.name}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{internetPackage.description}</Typography>
            <Typography variant="h6" fontWeight={700} sx={{ color: 'primary.main', mb: 1 }}>{formatCurrency(internetPackage.price)}</Typography>

            {isError ? (
              <Typography color="error">Failed to load usage data.</Typography>
            ) :
              <React.Fragment>
                <Box className={classes.usageText}>
                  <Typography variant="body2">{Math.round(totalDataUsed)}GB used</Typography>
                  <Typography variant="body2">
                    {isUnlimited ? 'Unlimited' : `${internetPackage.dataCap}GB total`}
                  </Typography>
                </Box>

                <Box sx={{ position: 'relative', mt: 1.5 }}>
                  <BorderLinearProgress
                    variant="determinate"
                    value={isUnlimited ? 100 : Math.min(usagePercentage, 100)}
                    usageColor={usageColor}
                  />
                  <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {!isUnlimited && (
                      <Typography variant="caption" sx={{ color: 'rgba(0,0,0,0.6)', fontWeight: 600 }}>
                        {`${Math.min(Math.round(usagePercentage), 100)}%`}
                      </Typography>
                    )}
                  </Box>
                  <UsageStatus isUnlimited={isUnlimited} usagePercentage={usagePercentage} />
                </Box>
              </React.Fragment>
            }

            <Stack direction="row" spacing={1.5} sx={{ mt: 2.5, pt: 1, alignItems: 'center', borderTop: '1px dashed rgba(0,0,0,0.1)' }}>
              <Box flexGrow={1} />
              {!isUnlimited && (
                <Tooltip title="Buy data booster">
                  <span>
                    <IconButton color="primary" onClick={handleBuyBooster} disabled={isPurchasingBooster} aria-label="buy booster" size="large">
                      {isPurchasingBooster ? <CircularProgress size={20} /> : <BoltIcon />}
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              <Tooltip title="Upgrade package">
                <span>
                  <IconButton color="secondary" onClick={openUpgrade} disabled={isUpgrading} aria-label="upgrade package" size="large">
                    {isUpgrading ? <CircularProgress size={20} /> : <UpgradeIcon />}
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Cancel subscription">
                <span>
                  <IconButton color="error" onClick={handleCancel} disabled={isCancelling} aria-label="cancel subscription" size="large">
                    {isCancelling ? <CircularProgress size={20} /> : <CancelIcon />}
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </CardContent>
        </Card>

      <Dialog open={upgradeOpen} onClose={closeUpgrade} fullWidth maxWidth="sm">
        <DialogTitle>Upgrade Internet Package</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel id="upgrade-package-label">New Package</InputLabel>
              <Select
                labelId="upgrade-package-label"
                label="New Package"
                value={selectedUpgradeId}
                onChange={(e) => setSelectedUpgradeId(e.target.value as string)}
              >
                {(allPackages || [])
                  .filter(p => p.id !== internetPackage.id)
                  .map(p => (
                    <MenuItem key={p.id} value={p.id!}>
                      {p.name} — {formatCurrency(p.price)} {p.dataCap ? `(${p.dataCap}GB cap)` : '(Unlimited)'}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            {selectedUpgradeId && (
              <Box sx={{ mt: 2 }}>
                {proration?.proRataCents != null ? (
                  <Typography variant="body2">
                    Prorated charge for this month: <strong>{formatCurrency((proration.proRataCents || 0) / 100)}</strong>
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary">Select a package to see prorated amount.</Typography>
                )}
              </Box>
            )}

            <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
              <Button onClick={closeUpgrade}>Close</Button>
              <Box flexGrow={1} />
              <Button onClick={confirmUpgrade} variant="contained" disabled={!selectedUpgradeId || isUpgrading}>
                {isUpgrading ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
                Confirm Upgrade
              </Button>
            </Stack>
          </Box>
        </DialogContent>
      </Dialog>

      <Snackbar open={snackOpen} autoHideDuration={4000} onClose={() => setSnackOpen(false)}>
        <Alert severity={snackSeverity} onClose={() => setSnackOpen(false)} sx={{ width: '100%' }}>
          {snackMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ManageInternetPackage;
